<template>
    <div class="relative">
        <div class="space-y-4" v-if="totalBusinessCount > 0">
            <div class="mb-8" v-for="user in businessEntities" :key="user.id">
                <div
                    class="flex items-center justify-between mb-2"
                    v-if="businessEntities.length > 1 && user.businesses.length > 0"
                >
                    <div class="flex items-center gap-2">
                        <Icon icon="person" />
                        <span class="text-sm text-slate-800 dark:text-slate-100">
                            {{ user.name }}'s Business Entities
                        </span>
                    </div>
                    <button
                        type="button"
                        class="btn btn-secondary !p-2 !py-1 text-sx flex items-center gap-2"
                        @click="openAddEditModal"
                    >
                        <Icon icon="business" :width="16" :height="16" />
                        Add Business Entity
                    </button>
                </div>
                <div
                    class="flex items-center justify-between mb-2"
                    v-else-if="businessEntities.length === 1 && user.businesses.length > 0"
                >
                    <div class="flex items-center gap-2">
                        <Icon icon="person" />
                        <span class="text-sm text-slate-800 dark:text-slate-100">
                            {{ user.name }}'s Business Entities
                        </span>
                    </div>
                    <button
                        type="button"
                        class="btn btn-secondary !p-2 !py-1 text-sx flex items-center gap-2"
                        @click="openAddEditModal"
                    >
                        <Icon icon="business" :width="16" :height="16" />
                        Add Business Entity
                    </button>
                </div>
                <div
                    class="flex items-center gap-2 mb-2"
                    v-else-if="user.businesses.length === 0"
                >
                    <Icon icon="person" />
                    <span class="text-sm text-slate-800 dark:text-slate-100">
                        {{ user.name }}'s Business Entities
                    </span>
                </div>
                <div class="" v-if="user.businesses.length === 0">
                    <p class="text-sm text-slate-500 dark:text-slate-400 mb-2">
                        Add a business entity to keep track of all related
                        cards.
                    </p>
                    <button
                        type="button"
                        class="btn btn-secondary !p-2 !py-1 text-sx flex items-center gap-2"
                        @click="openAddEditModal"
                    >
                        <Icon icon="business" :width="16" :height="16" />
                        Add Business Entity
                    </button>
                </div>
                <ul v-else class="w-full space-y-4">
                    <li
                        class="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 pt-4 first:border-none"
                        v-for="business in user.businesses"
                        :key="business.uuid"
                    >
                        <div class="flex items-center gap-2">
                            <div
                                class="w-10 h-10 rounded-full flex items-center justify-center bg-slate-200 dark:bg-slate-700"
                            >
                                <Icon icon="business" />
                            </div>
                            <button
                                type="button"
                                class="text-left hover:text-slate-400 transition-colors"
                                @click="handleEditBusinessModel(business)"
                            >
                                <h5 class="font-medium text-sm text-slate-800 dark:text-slate-100">
                                    {{ business.name }}
                                </h5>
                                <p class="text-sm text-slate-500 dark:text-slate-400">
                                    {{ businessTypeName(business.type) }}
                                </p>
                                <p class="text-xs text-slate-400 dark:text-slate-500" v-if="business.annual_business_spend">
                                    Annual spend: ${{ formatCurrency(business.annual_business_spend) }}
                                </p>
                            </button>
                        </div>
                        <div class="flex items-center gap-4">
                            <button
                                class="hover:text-slate-400 transition-colors"
                                @click.prevent="
                                    handleEditBusinessModel(business)
                                "
                            >
                                <Icon icon="edit" :width="20" :height="20" />
                                <span class="sr-only">Edit</span>
                            </button>
                            <button
                                class="hover:text-red-800 transition-colors"
                                @click.prevent="
                                    handleShowDeleteConfirm(business)
                                "
                            >
                                <Icon icon="delete" />
                                <span class="sr-only">Delete</span>
                            </button>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
        <div class="flex flex-col items-center justify-center" v-else>
            <div
                class="flex items-center justify-center w-14 h-14 rounded-full bg-slate-200 dark:bg-slate-700 mb-6"
            >
                <Icon icon="business" :width="24" :height="24" />
            </div>
            <h5 class="font-medium text-slate-800 dark:text-slate-100 mb-2">
                No business entities yet
            </h5>
            <p class="text-sm text-slate-500 dark:text-slate-400 mb-6">
                Add your first business entity to keep track of all related
                cards.
            </p>
            <button
                type="button"
                class="btn btn-secondary flex items-center gap-2"
                @click="openAddEditModal"
            >
                <Icon icon="business" :width="18" :height="18" />
                Add Business Entity
            </button>
        </div>
        <Teleport to="body">
            <BusinessAddEditModal
                :open="showBusinessAddEditModal"
                :users="businessEntities"
                v-model:businessModel="businessEntity"
                @close="handleBusinessEditModalClose"
                @add-business="handleAddBusiness"
                @update-business="handleUpdateBusiness"
            />
            <BusinessDeleteConfirmModal
                :open="showDeleteConfirmModal"
                @close="handleDeleteConfirmModalClose"
                @deleteBusiness="handleBusinessDeleted"
                :business="businessEntity"
            />
        </Teleport>
    </div>
</template>
<script setup>
import { computed, onMounted } from 'vue';
import BusinessAddEditModal from '@/components/business-entities/business-entities-add-edit-modal.vue';
import BusinessDeleteConfirmModal from '@/components/business-entities/business-entities-delete-confirm-modal.vue';
import Icon from '@/components/ui/icon/icon.vue';
import { useWalletStore } from '@/store/wallet';
import { useAppStore } from '@/store/app';
import { storeToRefs } from 'pinia';

const walletStore = useWalletStore();
const appStore = useAppStore();
const {
    businessEntity,
    businessEntities,
    showBusinessAddEditModal,
    showManageBusinessesModal,
    showDeleteConfirmModal,
} = storeToRefs(walletStore);

// Helper function for HubSpot tracking
const trackBusinessEntityEvent = (eventName, businessData, result = null) => {
    if (!window._hsq) return;
    
    const loggedInUser = appStore.userSettings;
    let userFirstName = null;
    let userLastName = null;

    if (loggedInUser && loggedInUser.name) {
        const nameParts = loggedInUser.name.split(' ');
        userFirstName = nameParts[0];
        if (nameParts.length > 1) {
            userLastName = nameParts.slice(1).join(' ');
        }
    }

    const eventProperties = {
        email: loggedInUser ? loggedInUser.email : null,
        firstname: userFirstName,
        lastname: userLastName,
        my10x_user_uuid: loggedInUser ? loggedInUser.uuid : null,
        business_name: businessData.name,
        business_type: businessData.type,
        tax_id_type: businessData.tax_id_type,
        annual_business_spend: businessData.annual_business_spend || null,
        business_entity_id: result?.data?.business?.id || result?.data?.id || businessData.id || businessData.uuid || null,
    };

    // Remove null/undefined properties
    for (const key in eventProperties) {
        if (eventProperties[key] === null || eventProperties[key] === undefined) {
            delete eventProperties[key];
        }
    }

    window._hsq.push([
        'trackCustomBehavioralEvent',
        {
            name: eventName,
            properties: eventProperties,
        },
    ]);
};
const {
    getBusinessEntitiesByUser,
    addBusiness,
    updateBusiness,
    deleteBusiness,
    resetBusinessEntity,
    setShowBusinessAddEditModal,
    setShowManageBusinessesModal,
    setShowDeleteConfirmModal,
    setBusinessEntity,
    setLoadingWallet,
} = walletStore;

const totalBusinessCount = computed(() => {
    return businessEntities.value.reduce((total, user) => {
        return total + user.businesses.length;
    }, 0);
});

const businessTypeName = (type) => {
    switch (type) {
        case 'sole-prop':
            return 'Sole Proprietorship';
        case 'partnership':
            return 'Partnership';
        case 'llc':
            return 'Limited Liability Company';
        case 's-corp':
            return 'S-Corp';
        case 'c-corp':
            return 'C-Corp';
        default:
            return 'Unknown';
    }
};

const formatCurrency = (value) => {
    if (!value) return '0';
    const num = parseFloat(value.toString().replace(/,/g, ''));
    return isNaN(num) ? '0' : num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const openAddEditModal = () => {
    setBusinessEntity(null);
    setShowBusinessAddEditModal(true);
    resetBusinessEntity();
};

const handleBusinessEditModalClose = () => {
    setShowBusinessAddEditModal(false);
    resetBusinessEntity();
};

const handleAddBusiness = async (businessData) => {
    const result = await addBusiness(businessData);
    
    // HubSpot Event Tracking for Add Business Entity
    if (result) {
        trackBusinessEntityEvent('pe22692776_added_business_entity', businessData, result);
    }
    
    await getBusinessEntitiesByUser();
    setShowBusinessAddEditModal(false);
    resetBusinessEntity();
};

const handleUpdateBusiness = async (businessData) => {
    const result = await updateBusiness(businessData);
    
    // HubSpot Event Tracking for Edit Business Entity
    if (result) {
        trackBusinessEntityEvent('pe22692776_edit_business_entity', businessData, result);
    }
    
    setShowBusinessAddEditModal(false);
    resetBusinessEntity();
};

const handleEditBusinessModel = (businessData) => {
    setBusinessEntity(businessData);
    setShowManageBusinessesModal(false);
    setShowBusinessAddEditModal(true);
};

const handleDeleteConfirmModalClose = () => {
    setShowDeleteConfirmModal(false);
    resetBusinessEntity();
};

const handleShowDeleteConfirm = (businessData) => {
    setBusinessEntity(businessData);
    setShowDeleteConfirmModal(true);
};

const handleBusinessDeleted = async (businessData) => {
    setLoadingWallet(true);
    try {
        const result = await deleteBusiness(businessData);
        
        // HubSpot Event Tracking for Delete Business Entity
        if (result) {
            trackBusinessEntityEvent('pe22692776_delete_business_entity', businessData, result);
        }
        
        await getBusinessEntitiesByUser();
        setShowDeleteConfirmModal(false);
        resetBusinessEntity();
    } catch (error) {
        console.error('Request failed!', error);
    } finally {
        setLoadingWallet(false);
    }
};

onMounted(() => {
    getBusinessEntitiesByUser();
});
</script>
